import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Plus, Trash2, Package, DollarSign, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { SearchPriceInput } from '@/components/form/search-price-input';
import { usePrice } from '@/lib/api/prices';
import { useProduct } from '@/lib/api/products';
import { Skeleton } from '@/components/ui/skeleton';
import { getCurrencyOption } from '@/lib/currency/currencyOptionsKeys';
import { formatMoney } from "@/lib/money";
import { useTranslation } from 'react-i18next';
import { trpc } from '@/lib/api/trpc';

interface CheckoutSessionBuilderProps {
  storeId: string;
  checkoutUrl: string;
  funnelId: string;
  stepId: string;
  environment: 'staging' | 'production';
  funnelVariantId?: string; // Force specific variant
  enableSticky?: boolean; // Enable sticky A/B selection
  onLineItemsChange?: (lineItems: Array<{ variantId: string; priceId: string; quantity: number }>) => void;
  onPreview?: (funnelSessionId: string, token: string, previewUrl: string) => void; // Deprecated - use global preview button
  onGenerate?: (checkoutToken: string) => void; // Legacy, deprecated
}

interface LineItem {
  productId: string;
  productName: string;
  variantId: string;
  variantName: string;
  imageUrl: string | null;
  priceId: string;
  quantity: number;
}

// Format price display
const formatPrice = (currencyOptions: Record<string, { amount: number }> | undefined, currency = 'USD') => {
  if (!currencyOptions || Object.keys(currencyOptions).length === 0) {
    return formatMoney(0, currency);
  }

  const resolved = getCurrencyOption(currencyOptions, currency);
  if (!resolved) return formatMoney(0, currency);

  return formatMoney(resolved.option.amount, resolved.currency);
};

// Format billing interval
const formatBillingInterval = (billingInterval: string | null, count: number | null) => {
  if (!billingInterval || billingInterval === 'one_time') return 'One-time';
  const intervalText = count && count > 1 ? `${count} ${billingInterval}s` : billingInterval;
  return `Every ${intervalText}`;
};

/**
 * Checkout Session Builder
 * Reuses the direct link generator logic and offer form pattern
 */
export function CheckoutSessionBuilder({
  storeId,
  checkoutUrl,
  funnelId,
  stepId,
  environment,
  funnelVariantId,
  enableSticky,
  onLineItemsChange,
  onPreview,
}: CheckoutSessionBuilderProps) {
  const [lineItems, setLineItems] = useState<LineItem[]>([]);
  const [showSearch, setShowSearch] = useState(false);
  const { t } = useTranslation('storefront');
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  // Backend preview session creation (deprecated - kept for backward compatibility)
  const createPreviewMutation = trpc.funnels.createPreviewSession.useMutation({
    onSuccess: (data) => {
      onPreview?.(data.sessionId, data.token, data.previewUrl);
    },
    onError: (error) => {
      console.error('❌ [CheckoutBuilder] Backend failed:', error);
      toast.error(t('checkoutSession.failedCreatePreview', { error: error.message }));
    },
  });

  // Notify parent when line items change
  React.useEffect(() => {
    if (onLineItemsChange) {
      const validItems = lineItems.filter((item) => item.variantId && item.priceId && item.quantity > 0);
      onLineItemsChange(
        validItems.map((item) => ({
          variantId: item.variantId,
          priceId: item.priceId,
          quantity: item.quantity,
        })),
      );
    }
  }, [lineItems, onLineItemsChange]);

  const handlePriceSelect = (selection: { priceId: string; productId: string; variantId: string }) => {
    // Check if this price is already in the list
    const exists = lineItems.some((item) => item.priceId === selection.priceId);
    if (exists) {
      return;
    }

    // Add new item
    const newLineItem: LineItem = {
      productId: selection.productId,
      productName: '', // Will be filled by LineItemLoader
      variantId: selection.variantId,
      variantName: '', // Will be filled by LineItemLoader
      imageUrl: null, // Will be filled by LineItemLoader
      priceId: selection.priceId,
      quantity: 1,
    };

    setLineItems([...lineItems, newLineItem]);
    setShowSearch(false);
  };

  const handlePriceEdit = (selection: { priceId: string; productId: string; variantId: string }) => {
    if (editingIndex === null) return;

    // Update existing item
    const updatedItems = [...lineItems];
    updatedItems[editingIndex] = {
      ...updatedItems[editingIndex],
      productId: selection.productId,
      productName: '', // Will be filled by LineItemLoader
      variantId: selection.variantId,
      variantName: '', // Will be filled by LineItemLoader
      imageUrl: null, // Will be filled by LineItemLoader
      priceId: selection.priceId,
    };
    setLineItems(updatedItems);
    setEditingIndex(null);
  };

  const handleQuantityChange = (index: number, quantity: number) => {
    const updatedItems = [...lineItems];
    updatedItems[index] = { ...updatedItems[index], quantity: Math.max(1, quantity) };
    setLineItems(updatedItems);
  };

  const handleRemoveItem = (index: number) => {
    const updatedItems = lineItems.filter((_, i) => i !== index);
    setLineItems(updatedItems);
    if (editingIndex === index) {
      setEditingIndex(null);
    }
  };

  const handleEditItem = (index: number) => {
    setEditingIndex(index);
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
  };

  // Deprecated: handlePreview is no longer used - line items are passed to parent via onLineItemsChange
  // Keeping for backward compatibility if onPreview is still provided
  const handlePreview = () => {
    if (!onPreview) return;

    const validItems = lineItems.filter((item) => item.variantId && item.priceId && item.quantity > 0);

    // Build builder options with line items (if any)
    const builderOptions: Record<string, any> = {};
    if (validItems.length > 0) {
      builderOptions.lineItems = validItems.map((item) => ({
        variantId: item.variantId,
        priceId: item.priceId,
        quantity: item.quantity,
      }));
    }

    // Call backend to create preview session with checkout resources
    createPreviewMutation.mutate({
      storeId,
      funnelId,
      stepId,
      builderOptions,
      environment,
      funnelVariantId,
      enableSticky,
    });
  };

  return (
    <div className="w-full space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">{t('checkoutSession.title')}</h3>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setShowSearch(!showSearch)}
          className="h-8"
        >
          <Plus className="mr-1 h-3.5 w-3.5" />
          {t('checkoutSession.addProduct')}
        </Button>
      </div>

      {showSearch && (
        <div className="bg-muted/30 space-y-2 rounded-lg border p-3">
          {/* A checkout session transacts specific products — per-order-SKU
              merchants keep every product inactive by design (TAG-1595). */}
          <SearchPriceInput storeId={storeId} onChange={handlePriceSelect} includeInactive />
          <p className="text-muted-foreground text-xs">
            {t('checkoutSession.searchHint')}
          </p>
        </div>
      )}

      {lineItems.length > 0 && (
        <div className="space-y-2">
          <p className="text-muted-foreground text-sm">{t('checkoutSession.selectedProducts', { count: lineItems.length })}</p>
          <div className="max-h-[400px] space-y-2 overflow-y-auto px-1 -mx-1">
            {lineItems.map((item, index) => (
              <LineItemLoader
                key={`${item.priceId}-${index}`}
                item={item}
                index={index}
                storeId={storeId}
                isEditing={editingIndex === index}
                onQuantityChange={handleQuantityChange}
                onRemove={handleRemoveItem}
                onEdit={handleEditItem}
                onCancelEdit={handleCancelEdit}
                onPriceChange={handlePriceEdit}
                onUpdate={(updatedItem) => {
                  const updatedItems = [...lineItems];
                  updatedItems[index] = updatedItem;
                  setLineItems(updatedItems);
                }}
              />
            ))}
          </div>
        </div>
      )}

      {lineItems.length === 0 && !showSearch && (
        <Card className="p-6">
          <div className="flex flex-col items-center justify-center text-center">
            <Package className="text-muted-foreground mb-2 h-8 w-8" />
            <p className="text-sm font-medium">{t('checkoutSession.noProducts')}</p>
            <p className="text-muted-foreground mt-1 text-xs">
              {t('checkoutSession.noProductsHint')}
            </p>
          </div>
        </Card>
      )}
    </div>
  );
}

// Line item display component
const LineItemDisplay = ({
  item,
  index,
  storeId,
  isEditing,
  onQuantityChange,
  onRemove,
  onEdit,
  onCancelEdit,
  onPriceChange,
}: {
  item: LineItem;
  index: number;
  storeId: string;
  isEditing: boolean;
  onQuantityChange: (index: number, quantity: number) => void;
  onRemove: (index: number) => void;
  onEdit: (index: number) => void;
  onCancelEdit: () => void;
  onPriceChange: (selection: { priceId: string; productId: string; variantId: string }) => void;
}) => {
  const { t } = useTranslation('storefront');
  const { data: price } = usePrice(item.priceId, true);

  if (isEditing) {
    return (
      <div className="bg-card space-y-2 rounded-md border p-3">
        <div className="flex items-center justify-between">
          <p className="text-sm font-medium">{t('checkoutSession.editPriceSelection')}</p>
          <Button type="button" variant="ghost" size="sm" onClick={onCancelEdit} className="h-7 text-xs">
            {t('checkoutSession.cancel')}
          </Button>
        </div>
        <SearchPriceInput storeId={storeId} priceId={item.priceId} onChange={onPriceChange} includeInactive />
      </div>
    );
  }

  return (
    <div className="bg-card flex items-start gap-3 rounded-md border p-3">
      <div className="bg-muted flex h-10 w-10 flex-shrink-0 items-center justify-center overflow-hidden rounded">
        {item.imageUrl ? (
          <img src={item.imageUrl} alt={item.productName} className="h-full w-full object-cover" />
        ) : (
          <Package className="text-muted-foreground h-5 w-5" />
        )}
      </div>
      <div className="min-w-0 flex-1 space-y-2">
        <div>
          <p className="truncate text-sm font-medium">{item.productName}</p>
          <p className="text-muted-foreground truncate text-xs">{item.variantName}</p>
          {price && (
            <div className="mt-0.5 flex items-center gap-1">
              <DollarSign className="text-muted-foreground h-3 w-3" />
              <p className="text-muted-foreground text-xs">
                {formatPrice(price.currencyOptions)} -{' '}
                {formatBillingInterval(price.billingInterval ?? null, price.billingIntervalCount ?? null)}
              </p>
            </div>
          )}
        </div>
        <div className="space-y-1">
          <Label htmlFor={`quantity-${index}`} className="text-xs">
            {t('checkoutSession.quantity')}
          </Label>
          <Input
            id={`quantity-${index}`}
            type="number"
            min="1"
            value={item.quantity}
            onChange={(e) => onQuantityChange(index, parseInt(e.target.value) || 1)}
            className="h-8 max-w-[100px] text-sm"
          />
        </div>
      </div>
      <div className="flex flex-shrink-0 flex-col gap-1">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => onEdit(index)}
          className="h-7 w-7 p-0"
          title={t('checkoutSession.edit')}
        >
          <Pencil className="h-3.5 w-3.5" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => onRemove(index)}
          className="h-7 w-7 p-0"
          title={t('checkoutSession.remove')}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
};

// Line item loader (fetches product/variant data)
const LineItemLoader = ({
  item,
  index,
  storeId,
  isEditing,
  onQuantityChange,
  onRemove,
  onEdit,
  onCancelEdit,
  onPriceChange,
  onUpdate,
}: {
  item: LineItem;
  index: number;
  storeId: string;
  isEditing: boolean;
  onQuantityChange: (index: number, quantity: number) => void;
  onRemove: (index: number) => void;
  onEdit: (index: number) => void;
  onCancelEdit: () => void;
  onPriceChange: (selection: { priceId: string; productId: string; variantId: string }) => void;
  onUpdate: (item: LineItem) => void;
}) => {
  const { data: price } = usePrice(item.priceId, true);
  const { data: product } = useProduct(item.productId, true);

  React.useEffect(() => {
    if (price && product && (!item.productName || !item.variantName)) {
      const variant = product.variants.find((v: any) => v.id === item.variantId);
      if (variant) {
        onUpdate({
          ...item,
          productName: product.name,
          variantName: variant.name,
          imageUrl: variant.imageUrl ?? product.variants.find((v: any) => v.default)?.imageUrl ?? null,
        });
      }
    }
  }, [price, product, item, onUpdate]);

  if (!item.productName || !item.variantName) {
    return (
      <div className="bg-card flex items-start gap-3 rounded-md border p-3">
        <Skeleton className="h-10 w-10 flex-shrink-0 rounded" />
        <div className="min-w-0 flex-1 space-y-2">
          <div className="space-y-1">
            <Skeleton className="h-4 w-28" />
            <Skeleton className="h-3 w-20" />
            <Skeleton className="h-3 w-32" />
          </div>
          <div className="space-y-1">
            <Skeleton className="h-3 w-14" />
            <Skeleton className="h-8 w-[100px]" />
          </div>
        </div>
        <div className="flex flex-shrink-0 flex-col gap-1">
          <Skeleton className="h-7 w-7" />
          <Skeleton className="h-7 w-7" />
        </div>
      </div>
    );
  }

  return (
    <LineItemDisplay
      item={item}
      index={index}
      storeId={storeId}
      isEditing={isEditing}
      onQuantityChange={onQuantityChange}
      onRemove={onRemove}
      onEdit={onEdit}
      onCancelEdit={onCancelEdit}
      onPriceChange={onPriceChange}
    />
  );
};
